import { BAUSTEIN_BASIS, BAUSTEIN_MODULE } from './portalBausteinSchema.ts';
import {
  BAUSTEIN_WERT_FREI,
  explicitSeatCeiling,
  resolveBausteinUnlock,
  type BausteinSeatInput,
} from './portalBausteinUnlock.ts';

/**
 * Klasse PORTAL_BAUSTEIN_SITZ. Fail-closed.
 * Ein Sitz kommt in einen Baustein nur bei exakt freigeschaltet.
 * Hintergrundanalyse hängt am Baustein ARKO, sie ist kein eigenes Modul.
 * Kalender, Akten, Dokumente und PDF haben hier keine Tür.
 * Die Baustein-Ansicht zeigt das Paket. Sie ist keine zweite Tür.
 */

export const SITZ_DOORS = [
  'arko',
  'diktat',
  'suche',
  'fallanalyse',
  'judikatur',
  'hintergrundanalyse',
] as const;

export type SitzDoor = (typeof SITZ_DOORS)[number];

/** Katalogname aus Abschnitt E. Suche ist LEXCORE-Fach, Fallanalyse ist Litigation. */
export const SITZ_DOOR_BAUSTEIN: Record<SitzDoor, string> = {
  arko: 'ARKO',
  diktat: 'Diktat',
  suche: 'LEXCORE-Fach',
  fallanalyse: 'Litigation',
  judikatur: 'JUDICATURE',
  hintergrundanalyse: 'ARKO',
};

const GATED_VIEWS: Record<string, SitzDoor> = {
  chat: 'arko',
  suche: 'suche',
  diktat: 'diktat',
  upload: 'diktat',
  litigation: 'fallanalyse',
};

export function seatEntersBaustein(door: SitzDoor, seat: BausteinSeatInput): boolean {
  const name = SITZ_DOOR_BAUSTEIN[door];
  return resolveBausteinUnlock(name, seat).kind === 'on';
}

/** Judikatur-Chip nur bei offener Tür. */
export function judicatureChipVisible(seat: BausteinSeatInput): boolean {
  return seatEntersBaustein('judikatur', seat);
}

/** Gesetztes max_seats, sonst included_seats. Ein Tarif füllt nichts auf. */
export function seatCeiling(seat: BausteinSeatInput): number | null {
  return explicitSeatCeiling(seat);
}

export function portalViewAllowed(view: string, seat: BausteinSeatInput): boolean {
  const door = GATED_VIEWS[view];
  if (!door) return true;
  return seatEntersBaustein(door, seat);
}

export function filterNavItemsForSeat<T extends { id: string }>(
  items: readonly T[],
  seat: BausteinSeatInput,
): T[] {
  return items.filter((item) => portalViewAllowed(item.id, seat));
}

export function seatInputFromCustomer(customer: {
  tier?: string | null;
  portal_role?: string | null;
  is_primary?: boolean | null;
  included_seats?: number | null;
  max_seats?: number | null;
  baustein_kategorien?: BausteinSeatInput['kategorien'];
  module_judicature?: boolean | null;
}): BausteinSeatInput {
  return {
    tier: customer.tier,
    portalRole: customer.portal_role,
    isPrimary: customer.is_primary === true,
    includedSeats: customer.included_seats,
    maxSeats: customer.max_seats,
    kategorien: customer.baustein_kategorien ?? null,
    moduleJudicature: customer.module_judicature === true,
  };
}

/** Namen aus Abschnitt E. Die Anzeige liest sie, sie öffnet keine Tür. */
export const SITZ_BASIS_NAMEN: readonly string[] = BAUSTEIN_BASIS.map((row) => row.name);
export const SITZ_MODUL_NAMEN: readonly string[] = BAUSTEIN_MODULE.map((row) => row.name);

export type BausteinAnzeigeGruppe = 'basis' | 'modul';

export interface BausteinAnzeigeZeile {
  gruppe: BausteinAnzeigeGruppe;
  name: string;
  offen: boolean;
}

function readKategorieMap(
  raw: BausteinSeatInput['kategorien'],
): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return null;
    try {
      const parsed = JSON.parse(text) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return null;
}

/** Ein Satz je Modul. Unpersönlich. Basis hat keinen. */
export const BAUSTEIN_MODUL_SATZ: Readonly<Record<string, string>> = {
  Sitze: 'Weitere Sitze der Kanzlei.',
  Diktat: 'Sprache wird zu Text.',
  ARKO: 'Der Chat für die Arbeit am Akt.',
  'LEXCORE-Fach': 'Suche im Normenbestand.',
  JUDICATURE: 'Entscheidungen des OGH.',
  Litigation: 'Fallanalyse.',
  'Support Plus': 'Erweiterter Support.',
};

export function bausteinModulSatz(name: string): string {
  return BAUSTEIN_MODUL_SATZ[name] ?? '';
}

/**
 * Längerer Text nur aus dem, was die Fläche schon kann.
 * Unpersönlich. Keine Rechtsgarantie. Die kurze Zeile bleibt in der Liste.
 */
export const BAUSTEIN_MODUL_TEXT: Readonly<Record<string, string>> = {
  Sitze: 'Weitere Personen der Kanzlei bekommen einen eigenen Sitz, bis zum gesetzten Deckel.',
  Diktat: 'Gesprochener Text wird zu einem Transkript.',
  ARKO: 'Fragen und Arbeit am Akt im Chat. Die Hintergrundanalyse lässt sich in den Chat öffnen, wenn dieser Baustein frei ist.',
  'LEXCORE-Fach': 'Suche im Normenbestand.',
  JUDICATURE: 'Passende Entscheidungen, nur wenn dieser Baustein frei ist. Der Chip bleibt sonst zu.',
  Litigation: 'Fallanalyse der Akte.',
  'Support Plus': 'Erweiterter Support. Vorgemerkt, kein eigener Schalter in der Leiste.',
};

export function bausteinModulText(name: string): string {
  return BAUSTEIN_MODUL_TEXT[name] ?? '';
}

/**
 * Beschreibung unter der Zeile. Modul: der vorhandene längere Text.
 * Basis: die vorhandene Notiz aus dem Schema. Sonst leer.
 */
export function bausteinBeschreibung(name: string): string {
  const modul = BAUSTEIN_MODUL_TEXT[name];
  if (modul) return modul;
  for (const row of BAUSTEIN_BASIS) {
    if (row.name === name && row.note) return row.note;
  }
  return '';
}

/** Nur eine Zeile. Dieselbe Zeile noch einmal schließt. Ohne Text bleibt der Stand. */
export function bausteinNaechsteZeile(offen: string, name: string): string {
  const next = name.trim();
  if (!next || !bausteinBeschreibung(next)) return offen;
  return offen === next ? '' : next;
}

/** Admin wie der Bestand: portal_role it_admin oder is_primary. */
export function bausteinDarfBestellen(
  seat: Pick<BausteinSeatInput, 'portalRole' | 'isPrimary'>,
): boolean {
  return seat.portalRole === 'it_admin' || seat.isPrimary === true;
}

/** Knopf nur am geschlossenen Modul ohne gespeicherte Anfrage. Basis nie. Ohne Admin nie. */
export function bausteinZeigtBestellen(
  gruppe: BausteinAnzeigeGruppe,
  offen: boolean,
  seat: Pick<BausteinSeatInput, 'portalRole' | 'isPrimary'> & Partial<Pick<BausteinSeatInput, 'kategorien'>>,
  name = '',
): boolean {
  if (gruppe !== 'modul' || offen) return false;
  if (name && bausteinZeileAnfrage(name, seat)) return false;
  return bausteinDarfBestellen(seat);
}

/** Knopf nur am offenen Modul ohne gespeicherte Anfrage. Basis nie. Ohne Admin nie. */
export function bausteinZeigtKuendigen(
  gruppe: BausteinAnzeigeGruppe,
  offen: boolean,
  seat: Pick<BausteinSeatInput, 'portalRole' | 'isPrimary'> & Partial<Pick<BausteinSeatInput, 'kategorien'>>,
  name = '',
): boolean {
  if (gruppe !== 'modul' || !offen) return false;
  if (name && bausteinZeileAnfrage(name, seat)) return false;
  return bausteinDarfBestellen(seat);
}

/** Zugehörige Türen sind zu. Ein Modul ohne Tür gilt als zu. */
export function bausteinTuerenZu(name: string, seat: BausteinSeatInput): boolean {
  for (const door of SITZ_DOORS) {
    if (SITZ_DOOR_BAUSTEIN[door] !== name) continue;
    if (seatEntersBaustein(door, seat)) return false;
  }
  return true;
}

/**
 * Anfrage liegt neben dem Modulnamen. Der Wert ist nicht freigeschaltet.
 * Die Tür liest diesen Schlüssel nicht.
 */
export const BAUSTEIN_ANFRAGE_WERT = 'angefragt';

export function bausteinAnfrageSchluessel(modul: string): string {
  return `anfrage:${modul}`;
}

function copyKategorieStrings(raw: BausteinSeatInput['kategorien']): Record<string, string> {
  const map = readKategorieMap(raw);
  const next: Record<string, string> = {};
  if (!map) return next;
  for (const [key, value] of Object.entries(map)) {
    if (typeof value === 'string') next[key] = value;
  }
  return next;
}

/** Die Zeile hat eine gespeicherte Anfrage. Basis und fremde Namen nie. */
export function bausteinZeileAnfrage(name: string, seat: BausteinSeatInput): boolean {
  if (!SITZ_MODUL_NAMEN.includes(name)) return false;
  const map = readKategorieMap(seat.kategorien);
  if (!map) return false;
  const value = map[bausteinAnfrageSchluessel(name)];
  return typeof value === 'string' && value.trim() === BAUSTEIN_ANFRAGE_WERT;
}

/**
 * Nur dieser Modul-Schlüssel bekommt eine Anfrage.
 * Der Modulwert bleibt. Fehlendes Feld: nur der Anfrage-Schlüssel.
 * Steht der Schlüssel schon auf freigeschaltet: kein Schreibsatz.
 * Steht die Anfrage schon: kein zweiter Schreibsatz, keine zweite Mail.
 * Basis und unbekannte Namen: kein Schreibsatz.
 */
export function bausteinKategorienNachBestellung(
  raw: BausteinSeatInput['kategorien'],
  modul: string,
): Record<string, string> | null {
  if (!SITZ_MODUL_NAMEN.includes(modul)) return null;
  const next = copyKategorieStrings(raw);
  if ((next[modul] ?? '').trim() === BAUSTEIN_WERT_FREI) return null;
  const schluessel = bausteinAnfrageSchluessel(modul);
  if ((next[schluessel] ?? '').trim() === BAUSTEIN_ANFRAGE_WERT) return null;
  next[schluessel] = BAUSTEIN_ANFRAGE_WERT;
  return next;
}

/**
 * Nur dieser Modul-Schlüssel bekommt eine Anfrage.
 * Der Wert freigeschaltet bleibt. Andere Schlüssel bleiben.
 * Steht der Schlüssel nicht auf freigeschaltet: kein Schreibsatz.
 * Steht die Anfrage schon: kein zweiter Schreibsatz. freigeschaltet bleibt unberührt.
 * Basis und unbekannte Namen: kein Schreibsatz.
 */
export function bausteinKategorienNachKuendigung(
  raw: BausteinSeatInput['kategorien'],
  modul: string,
): Record<string, string> | null {
  if (!SITZ_MODUL_NAMEN.includes(modul)) return null;
  const next = copyKategorieStrings(raw);
  if ((next[modul] ?? '').trim() !== BAUSTEIN_WERT_FREI) return null;
  const schluessel = bausteinAnfrageSchluessel(modul);
  if ((next[schluessel] ?? '').trim() === BAUSTEIN_ANFRAGE_WERT) return null;
  next[schluessel] = BAUSTEIN_ANFRAGE_WERT;
  return next;
}

/** Nur ein Modulname mit exakt freigeschaltet. Ein unbekannter Schlüssel ist zu. */
export function bausteinModulIstFrei(name: string, seat: BausteinSeatInput): boolean {
  if (!SITZ_MODUL_NAMEN.includes(name)) return false;
  const map = readKategorieMap(seat.kategorien);
  if (!map || !Object.prototype.hasOwnProperty.call(map, name)) return false;
  const value = map[name];
  if (typeof value !== 'string') return false;
  return value.trim() === BAUSTEIN_WERT_FREI;
}

/** Anfrage ist da, und jede Tür dieses Moduls steht wie vorher. */
export function bausteinAnfrageBleibt(
  modul: string,
  vorher: BausteinSeatInput,
  nachher: BausteinSeatInput,
): boolean {
  if (!SITZ_MODUL_NAMEN.includes(modul)) return false;
  if (!bausteinZeileAnfrage(modul, nachher)) return false;
  if (bausteinModulIstFrei(modul, vorher) !== bausteinModulIstFrei(modul, nachher)) return false;
  for (const door of SITZ_DOORS) {
    if (SITZ_DOOR_BAUSTEIN[door] !== modul) continue;
    if (seatEntersBaustein(door, vorher) !== seatEntersBaustein(door, nachher)) return false;
  }
  return true;
}

/**
 * Zwei Gruppen. Basis ist immer dabei und offen.
 * Ein Modul ist nur offen, wenn das Feld exakt freigeschaltet sagt.
 * Zeilen außerhalb von Basis und Modul fehlen.
 */
export function bausteinSitzAnzeige(seat: BausteinSeatInput): BausteinAnzeigeZeile[] {
  const basis = SITZ_BASIS_NAMEN.map((name) => ({
    gruppe: 'basis' as const,
    name,
    offen: true,
  }));
  const modul = SITZ_MODUL_NAMEN.map((name) => ({
    gruppe: 'modul' as const,
    name,
    offen: bausteinModulIstFrei(name, seat),
  }));
  return [...basis, ...modul];
}

/** Öffentliche Office-Adresse aus dem Bestand. Kein Secret. */
export const BAUSTEIN_OFFICE_MAIL = 'office@arkorbit.at';

/** Gleicher Brevo-SMTP-Weg wie die Systemmails. Kein zweiter Schlüssel. */
export const BAUSTEIN_MAILWEG = 'https://api.brevo.com/v3/smtp/email';

export type BausteinAnfrageArt = 'Freischalten' | 'Kündigung';

export interface BausteinAnfrageBrief {
  to: string;
  subject: string;
  text: string;
}

const ANFRAGE_ARTEN: readonly BausteinAnfrageArt[] = ['Freischalten', 'Kündigung'];

function eineZeile(value: string | null | undefined): string {
  return String(value || '').replace(/[\r\n\t]+/g, ' ').trim().slice(0, 120);
}

/** Nur die Zustimmung schreibt eine Kündigungs-Anfrage. Der erste Klick und Abbrechen nicht. */
export function bausteinKuendigungDarfSchreiben(zugestimmt: boolean): boolean {
  return zugestimmt === true;
}

/**
 * Kanzlei oder Sitz, Modul, Art. Ziel ist die Office-Adresse.
 * Kein Passwort, kein Token, kein .env. Basis und fremde Namen: kein Brief.
 */
export function bausteinAnfrageBrief(input: {
  kanzlei?: string | null;
  sitz?: string | null;
  modul: string;
  art: string;
}): BausteinAnfrageBrief | null {
  const modul = input.modul.trim();
  if (!SITZ_MODUL_NAMEN.includes(modul)) return null;
  if (!ANFRAGE_ARTEN.includes(input.art as BausteinAnfrageArt)) return null;
  const kanzlei = eineZeile(input.kanzlei);
  const sitz = eineZeile(input.sitz);
  if (!kanzlei && !sitz) return null;
  const text = [
    'Kanzlei: ' + (kanzlei || sitz),
    'Sitz: ' + (sitz || kanzlei),
    'Modul: ' + modul,
    'Anfrage: ' + input.art,
  ].join('\n');
  return {
    to: BAUSTEIN_OFFICE_MAIL,
    subject: 'Baustein-Anfrage ' + input.art + ': ' + modul,
    text,
  };
}

/** Körper für den bestehenden Brevo-Weg. Empfänger bleibt die Office-Adresse. */
export function bausteinAnfrageBrevo(brief: BausteinAnfrageBrief): {
  url: string;
  body: {
    sender: { name: string; email: string };
    to: { email: string }[];
    subject: string;
    textContent: string;
  };
} {
  return {
    url: BAUSTEIN_MAILWEG,
    body: {
      sender: { name: 'ARKORBIT', email: BAUSTEIN_OFFICE_MAIL },
      to: [{ email: BAUSTEIN_OFFICE_MAIL }],
      subject: brief.subject,
      textContent: brief.text,
    },
  };
}

/**
 * Sendet nur mit dem vorhandenen Schlüssel des Aufrufers.
 * Ohne Schlüssel bleibt die gespeicherte Anfrage, es geht nichts raus.
 */
export async function sendeBausteinAnfrageMail(
  brief: BausteinAnfrageBrief,
  weg?: {
    key?: string | null;
    fetchImpl?: (url: string, init: { method: string; headers: Record<string, string>; body: string }) => Promise<{ ok: boolean }>;
  } | null,
): Promise<boolean> {
  const raw = String(weg?.key || '');
  const key = (raw.split(/\r?\n/)[0] || '').trim().replace(/^["']|["']$/g, '');
  if (!key) return false;
  if (brief.to !== BAUSTEIN_OFFICE_MAIL) return false;
  const aufruf = bausteinAnfrageBrevo(brief);
  if (aufruf.body.to[0]?.email !== BAUSTEIN_OFFICE_MAIL) return false;
  const fetchImpl = weg?.fetchImpl
    || (typeof globalThis.fetch === 'function'
      ? (url: string, init: { method: string; headers: Record<string, string>; body: string }) =>
          globalThis.fetch(url, init)
      : null);
  if (!fetchImpl) return false;
  try {
    const res = await fetchImpl(aufruf.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': key,
      },
      body: JSON.stringify(aufruf.body),
    });
    return res.ok === true;
  } catch {
    return false;
  }
}
